import { useEffect, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { FileUploader } from "@/shared/components";
import { useDocumentSuggest, useFormDraft } from "@/shared/hooks";
import { Button, Input, Toast, SearchableSelect } from "@/shared/ui";
import { applyOcrFields } from "@/shared/utils";
import { BANKS, type Bank } from "@/shared/consts/banks";
import { hasAnyValue } from "../partialSave";
import { useGetBankingQuery, useSubmitBankingMutation } from "./api";
import type { BankingDto } from "./types";
import BankingFormSkeleton from "./BankingFormSkeleton";
import { type BankingFormValues, bankingSchema } from "./validation";

interface Props {
  token: string;
  onNext: () => void;
}

export default function BankingForm({ token, onNext }: Props) {
  const { t } = useTranslation("ApplyApplicationPage");

  const { data, isLoading, error } = useGetBankingQuery(token);
  const [submitBanking, { isLoading: isSaving }] = useSubmitBankingMutation();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [toast, setToast] = useState<{
    text: string;
    color: "positive" | "negative" | "notice" | "grey";
  } | null>(null);

  const [isCertRemoved, setIsCertRemoved] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [ocrFilled, setOcrFilled] = useState<Set<string>>(new Set());
  const { scan: scanDocument, state: ocrState, reset: resetOcr } = useDocumentSuggest(token);

  const methods = useForm<BankingFormValues>({
    resolver: zodResolver(bankingSchema),
    defaultValues: {
      bank_name: "",
      iban_account: "",
      bik_number: "",
      bank_certificate_file: null,
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = methods;

  const { applyDraft, hasValidDraft, clearDraft } = useFormDraft<BankingFormValues>({
    key: `onboarding:banking:${token}`,
    form: methods,
    schemaVersion: "v1",
    ignoreFields: ["bank_certificate_file"],
  });

  const bankFromData = useMemo(() => {
    if (!data) return null;
    const foundByBik = data.bik_number ? BANKS.find((bank) => bank.bik === data.bik_number) : null;
    if (foundByBik) return foundByBik;

    const foundByName = data.bank_name ? BANKS.find((bank) => bank.name === data.bank_name) : null;
    return foundByName || null;
  }, [data]);

  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (data && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      setIsCertRemoved(false);
      if (bankFromData) {
        setSelectedBank(bankFromData);
        reset({
          bank_name: bankFromData.name,
          iban_account: data.iban_account ?? "",
          bik_number: bankFromData.bik,
          bank_certificate_file: null,
        });
      } else {
        setSelectedBank(null);
        reset({
          bank_name: data.bank_name ?? "",
          iban_account: data.iban_account ?? "",
          bik_number: data.bik_number ?? "",
          bank_certificate_file: null,
        });
      }
      if (hasValidDraft) applyDraft();
    } else if (!data && hasValidDraft) {
      applyDraft();
    }
  }, [data, reset, bankFromData, hasValidDraft, applyDraft]);

  function handleBankChange(bank: Bank | null) {
    setSelectedBank(bank);
    if (bank) {
      setValue("bank_name", bank.name, { shouldValidate: true });
      setValue("bik_number", bank.bik, { shouldValidate: true });
    } else {
      setValue("bank_name", "", { shouldValidate: true });
      setValue("bik_number", "", { shouldValidate: true });
    }
  }

  function resolveBankFromOcr(fields: Record<string, string>): Bank | null {
    const byBik = fields.bik_number ? BANKS.find((b) => b.bik === fields.bik_number) : null;
    if (byBik) return byBik;
    const byName = fields.bank_name ? BANKS.find((b) => b.name === fields.bank_name) : null;
    return byName ?? null;
  }

  async function runBankOcr(uploadedFile: File) {
    const result = await scanDocument(uploadedFile, "bank_certificate");
    if (result.status === "idle") return;
    const fields = result.fields ?? {};

    const matched =
      (fields.iban_account?.trim() ? 1 : 0) + (fields.bank_name?.trim() || fields.bik_number?.trim() ? 1 : 0);
    if (matched * 2 < 2) {
      setToast({ color: "notice", text: "banking.ocr.wrongFile.bank_certificate" });
      return;
    }

    const filled: string[] = [];

    const ibanResult = applyOcrFields({ iban_account: getValues("iban_account") ?? "" }, fields, ["iban_account"]);
    if (ibanResult.filled.length > 0) {
      setValue("iban_account", ibanResult.next.iban_account, { shouldValidate: true });
      filled.push("iban_account");
    }

    if ((getValues("bank_name") ?? "").trim() === "") {
      const matchedBank = resolveBankFromOcr(fields);
      if (matchedBank) {
        handleBankChange(matchedBank);
        filled.push("bank_name");
      }
    }

    if (filled.length > 0) {
      setOcrFilled(new Set(filled));
      setToast({ color: "positive", text: "banking.ocr.filled" });
    }
  }

  function handleCertChange(uploadedFile: File | null) {
    setValue("bank_certificate_file", uploadedFile, { shouldValidate: true });
    if (uploadedFile) {
      setIsCertRemoved(false);
      void runBankOcr(uploadedFile);
    } else {
      resetOcr();
      setOcrFilled(new Set());
    }
  }

  async function handleSave() {
    if (isSaving) return;
    const values = getValues();

    const partial: Partial<BankingDto> = {};

    if (values.bank_name) partial.bank_name = values.bank_name;
    if (values.iban_account) partial.iban_account = values.iban_account;
    if (values.bik_number) partial.bik_number = values.bik_number;

    const hasCertFromApi = !!data?.bank_certificate_file_url && !isCertRemoved;
    if (!hasCertFromApi && values.bank_certificate_file) partial.bank_certificate_file = values.bank_certificate_file;

    if (!hasAnyValue(partial)) return;

    try {
      setIsSavingDraft(true);
      await submitBanking({ token, body: partial }).unwrap();
      setToast({ color: "positive", text: "banking.toast.success" });
    } catch {
      setToast({ color: "negative", text: "banking.toast.error" });
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function onSubmit(values: BankingFormValues) {
    if (isSaving) return;

    try {
      const hasFromApi = !!data?.bank_certificate_file_url && !isCertRemoved;
      const hasNewFile = !!values.bank_certificate_file;

      if (!hasFromApi && !hasNewFile) {
        setToast({ color: "negative", text: "validation.banking.file_required" });
        return;
      }

      await submitBanking({
        token,
        body: {
          bank_name: values.bank_name,
          iban_account: values.iban_account,
          bik_number: values.bik_number,
          bank_certificate_file: hasFromApi ? null : values.bank_certificate_file,
        },
      }).unwrap();

      clearDraft();
      setToast({ color: "positive", text: "banking.toast.success" });
      onNext();
    } catch {
      setToast({ color: "negative", text: "banking.toast.error" });
    }
  }

  const file = watch("bank_certificate_file");
  const isNotFound = error && "status" in error && error.status === 404;

  if (isLoading && !data && !isNotFound) return <BankingFormSkeleton />;

  return (
    <form className="flex flex-col justify-between min-h-[620px] gap-7" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-7">
        <h2 className="text-display-2xs content-base-primary">{t("banking.title")}</h2>

        <div className="flex flex-col gap-4">
          <FileUploader
            label={t("banking.certificate.label")}
            hasRequiredLabel
            value={file}
            existingFileUrl={!isCertRemoved ? data?.bank_certificate_file_url : null}
            onChange={handleCertChange}
            onRemoveExisting={() => {
              setIsCertRemoved(true);
              setValue("bank_certificate_file", null);
              resetOcr();
              setOcrFilled(new Set());
            }}
            maxSizeMB={50}
            accept=".pdf,.docx,.jpg,.jpeg,.png"
            error={errors.bank_certificate_file?.message ? t(errors.bank_certificate_file.message as string) : null}
          />

          {ocrState === "scanning" ? (
            <Toast color="grey" text={t("banking.ocr.scanning")} closable={false} autoClose={false} isFullWidth />
          ) : (
            <Toast color="grey" text={t("banking.ocr.hint")} closable={false} autoClose={false} isFullWidth />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect<Bank>
              label={t("banking.bank_name.label")}
              hasRequiredLabel
              placeholder={t("banking.bank_name.placeholder")}
              searchPlaceholder={t("banking.bank_name.searchPlaceholder")}
              options={BANKS}
              value={selectedBank}
              onChange={handleBankChange}
              searchKeys={["name", "bik"]}
              displayKey="name"
              highlight={ocrFilled.has("bank_name")}
              error={
                errors.bank_name?.message
                  ? t(errors.bank_name.message)
                  : errors.bik_number?.message
                    ? t(errors.bik_number.message)
                    : undefined
              }
            />
            <Input
              label={t("banking.iban.label")}
              hasRequiredLabel
              placeholder={t("banking.iban.placeholder")}
              highlight={ocrFilled.has("iban_account")}
              {...register("iban_account")}
              error={errors.iban_account?.message ? t(errors.iban_account.message) : undefined}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-3">
        {toast && (
          <Toast
            key={`${toast.color}-${toast.text}`}
            color={toast.color}
            text={t(toast.text)}
            onClose={() => setToast(null)}
          />
        )}

        <Button variant="secondary" size="lg" type="button" onClick={handleSave} disabled={isSaving}>
          {isSavingDraft ? t("banking.saving") : t("banking.save")}
        </Button>

        <Button variant="primary" size="lg" type="submit" disabled={isSaving}>
          {isSaving && !isSavingDraft ? t("banking.saving") : t("banking.next_section")}
          {(!isSaving || isSavingDraft) && (
            <span className="text-white">
              <ArrowRightIcon />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
